import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@env/environment';

/**
 * API Service - Service chung để xử lý tất cả HTTP requests
 * Tự động thêm Authorization header và xử lý errors
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private http = inject(HttpClient);
  private readonly API_URL = environment.apiUrl || 'https://localhost:7130/api';

  /**
   * Lấy token từ localStorage
   */
  private getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Tạo headers với Authorization nếu có token
   */
  private createHeaders(customHeaders?: HttpHeaders): HttpHeaders {
    let headers = customHeaders || new HttpHeaders();

    // Set default Content-Type
    if (!headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'application/json');
    }

    // Set Accept header
    if (!headers.has('accept')) {
      headers = headers.set('accept', '*/*');
    }

    // Thêm Authorization header nếu có token
    const token = this.getToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  /**
   * Xử lý errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'Đã xảy ra lỗi!';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Lỗi: ${error.error.message}`;
    } else {
      // Server-side error
      switch (error.status) {
        case 400:
          errorMessage = error.error?.message || 'Dữ liệu không hợp lệ!';
          break;
        case 401:
          errorMessage = 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!';
          // Clear token and redirect to login
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_info');
          window.location.href = '/admin/login';
          break;
        case 403:
          errorMessage = 'Bạn không có quyền truy cập!';
          break;
        case 404:
          errorMessage = 'Không tìm thấy dữ liệu!';
          break;
        case 500:
          errorMessage = 'Lỗi server. Vui lòng thử lại sau!';
          break;
        default:
          errorMessage = error.error?.message || `Lỗi: ${error.status}`;
      }
    }

    console.error('API Error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }

  /**
   * GET request
   */
  get<T>(endpoint: string, customHeaders?: HttpHeaders): Observable<T> {
    const headers = this.createHeaders(customHeaders);
    return this.http.get<T>(`${this.API_URL}/${endpoint}`, { headers });
  }

  /**
   * POST request
   */
  post<T>(endpoint: string, body: any, customHeaders?: HttpHeaders): Observable<T> {
    const headers = this.createHeaders(customHeaders);
    return this.http.post<T>(`${this.API_URL}/${endpoint}`, body, { headers });
  }

  /**
   * PUT request
   */
  put<T>(endpoint: string, body: any, customHeaders?: HttpHeaders): Observable<T> {
    const headers = this.createHeaders(customHeaders);
    return this.http.put<T>(`${this.API_URL}/${endpoint}`, body, { headers });
  }

  /**
   * DELETE request
   */
  delete<T>(endpoint: string, customHeaders?: HttpHeaders): Observable<T> {
    const headers = this.createHeaders(customHeaders);
    return this.http.delete<T>(`${this.API_URL}/${endpoint}`, { headers });
  }

  /**
   * PATCH request
   */
  patch<T>(endpoint: string, body: any, customHeaders?: HttpHeaders): Observable<T> {
    const headers = this.createHeaders(customHeaders);
    return this.http.patch<T>(`${this.API_URL}/${endpoint}`, body, { headers });
  }

  /**
   * POST request without auth (for login, register)
   */
  postPublic<T>(endpoint: string, body: any): Observable<T> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: '*/*',
    });

    return this.http
      .post<T>(`${this.API_URL}/${endpoint}`, body, { headers })
      .pipe(catchError(this.handleError));
  }

  /**
   * Upload files with multipart/form-data
   */
  upload<T>(endpoint: string, formData: FormData): Observable<T> {
    let headers = new HttpHeaders();
    headers = headers.set('accept', 'application/json');
    // Không set Content-Type để Angular tự động xử lý multipart/form-data
    const token = this.getToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http
      .post<T>(`${this.API_URL}/${endpoint}`, formData, { headers })
      .pipe(catchError(this.handleError));
  }
}
